import { useRef, useCallback, useEffect, useState } from "react";
import type { MediaController } from "./types";

declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          events: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number }) => void;
          };
          playerVars?: Record<string, number | string>;
        }
      ) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead?: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  destroy(): void;
}

function getYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
}

let apiLoaded = false;
let apiLoading = false;
const apiReadyCallbacks: (() => void)[] = [];

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (apiLoaded) {
      resolve();
      return;
    }

    apiReadyCallbacks.push(resolve);

    if (apiLoading) return;
    apiLoading = true;

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      apiLoaded = true;
      apiReadyCallbacks.forEach((cb) => cb());
      apiReadyCallbacks.length = 0;
    };
  });
}

export function useYouTubeAdapter(url: string): {
  containerId: string;
  controller: MediaController | null;
} {
  const playerRef = useRef<YTPlayer | null>(null);
  const [ready, setReady] = useState(false);
  const containerId = useRef(`yt-player-${Math.random().toString(36).slice(2)}`).current;
  const timeUpdateCallbacks = useRef<Set<(time: number) => void>>(new Set());
  const stateChangeCallbacks = useRef<Set<(playing: boolean) => void>>(new Set());
  const intervalRef = useRef<number | null>(null);

  const videoId = getYouTubeVideoId(url);

  useEffect(() => {
    if (!videoId) return;

    let mounted = true;

    loadYouTubeAPI().then(() => {
      if (!mounted) return;

      // Wait for container to be in DOM
      const checkContainer = setInterval(() => {
        if (document.getElementById(containerId)) {
          clearInterval(checkContainer);

          playerRef.current = new window.YT.Player(containerId, {
            videoId,
            playerVars: {
              autoplay: 0,
              modestbranding: 1,
              rel: 0,
            },
            events: {
              onReady: () => {
                if (mounted) setReady(true);
              },
              onStateChange: (event) => {
                const playing = event.data === window.YT.PlayerState.PLAYING;
                stateChangeCallbacks.current.forEach((cb) => cb(playing));
              },
            },
          });

          // Poll for time updates (YT API doesn't have timeupdate event)
          intervalRef.current = window.setInterval(() => {
            if (playerRef.current) {
              const time = playerRef.current.getCurrentTime();
              timeUpdateCallbacks.current.forEach((cb) => cb(time));
            }
          }, 250);
        }
      }, 50);
    });

    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      playerRef.current?.destroy();
    };
  }, [videoId, containerId]);

  const play = useCallback(() => {
    playerRef.current?.playVideo();
  }, []);

  const pause = useCallback(() => {
    playerRef.current?.pauseVideo();
  }, []);

  const seek = useCallback((seconds: number) => {
    playerRef.current?.seekTo(seconds, true);
  }, []);

  const getCurrentTime = useCallback(() => {
    return playerRef.current?.getCurrentTime() ?? 0;
  }, []);

  const getDuration = useCallback(() => {
    return playerRef.current?.getDuration() ?? 0;
  }, []);

  const isPlaying = useCallback(() => {
    return playerRef.current?.getPlayerState() === window.YT?.PlayerState?.PLAYING;
  }, []);

  const onTimeUpdate = useCallback((cb: (time: number) => void) => {
    timeUpdateCallbacks.current.add(cb);
    return () => {
      timeUpdateCallbacks.current.delete(cb);
    };
  }, []);

  const onStateChange = useCallback((cb: (playing: boolean) => void) => {
    stateChangeCallbacks.current.add(cb);
    return () => {
      stateChangeCallbacks.current.delete(cb);
    };
  }, []);

  const controller: MediaController | null = ready
    ? {
        play,
        pause,
        seek,
        getCurrentTime,
        getDuration,
        isPlaying,
        onTimeUpdate,
        onStateChange,
        src: url,
        type: "youtube",
      }
    : null;

  return { containerId, controller };
}
